import type { DirectorRequestDetail } from '@/types/director'
import { lessonRange } from '@/components/effective-plan/planFormat'
import { sortLessonsByDateTime } from '@/utils/lessonSort'

interface CaseLessonsTableProps {
  detail: DirectorRequestDetail
}

function formatTableDate(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('de-CH', { weekday: 'short', day: '2-digit', month: '2-digit', year: '2-digit' })
  } catch {
    return iso
  }
}

/** Zugewiesene Lektionen gelten als vergeben (V6/B.1-P5). */
const COMMITTED = ['accepted', 'assigned', 'filled', 'booked']

/** Fallakte v2 (P5.5): Lektionen-Tabelle der Fallakte. */
export function CaseLessonsTable({ detail }: CaseLessonsTableProps) {
  const rows = sortLessonsByDateTime(detail.lessons)

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
        <h2 className="text-sm font-bold">Betroffene Lektionen</h2>
        <span className="text-xs text-gray-400">{rows.length} · Zeitraum {formatTableDate(detail.date)}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" aria-label="Lektionen der Anfrage">
          <thead>
            <tr className="bg-gray-50 text-gray-500">
              <th className="px-4 py-2 text-left font-semibold">Zeit</th>
              <th className="px-4 py-2 text-left font-semibold">Fach</th>
              <th className="px-4 py-2 text-left font-semibold">Klasse</th>
              <th className="px-4 py-2 text-left font-semibold">Raum</th>
              <th className="px-4 py-2 text-left font-semibold">Vertretung</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map(l => {
              const isAssigned = Boolean(l.assigned_teacher_id && COMMITTED.includes(l.status ?? ''))
              const time = lessonRange(l)
              return (
                <tr key={l.id} className={l.status === 'cancelled' ? 'opacity-50' : ''}>
                  <td className="px-4 py-2 font-medium whitespace-nowrap">
                    {formatTableDate(l.date)} · {time || '—'}
                  </td>
                  <td className="px-4 py-2">{l.subject_name || '—'}</td>
                  <td className="px-4 py-2">{l.klass_name || '—'}</td>
                  <td className="px-4 py-2">{l.room_name || '—'}</td>
                  <td className="px-4 py-2">
                    {isAssigned
                      ? <span className="text-green-700">✓ {l.assigned_teacher_name || 'Vergeben'}</span>
                      : l.status === 'cancelled'
                        ? <span className="text-gray-400">✗ Storniert</span>
                        : l.status === 'director_removed'
                          ? <span className="text-gray-400">— Entfernt</span>
                          : <span className="text-gray-400">— offen</span>}
                  </td>
                </tr>
              )
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-400">Keine Lektionen</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
